import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, MessageSquare, Wrench, Clock, CheckCircle2, PhoneCall } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useData } from '../contexts/DataContext';
import { useToast } from '../hooks/use-toast';

const StatusPill = ({ status }) => {
  const styles = {
    'Submitted': 'bg-[#E8DDFF] text-[#1F0068]',
    'Assigned': 'bg-[#FFEDD6] text-[#1F0068]',
    'Engineer on the way': 'bg-[#FCE0EC] text-[#1F0068]',
    'In progress': 'bg-[#FF5C39] text-white',
    'Resolved': 'bg-[#00b67a] text-white',
  };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${styles[status] || 'bg-[#FFF6EB] text-[#1F0068]'}`}>{status}</span>;
};

const ClaimDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { claims, advanceStatus } = useData();
  const claim = claims.find((c) => c.id === id);
  const [note, setNote] = useState('');
  const { toast } = useToast();

  if (!claim) {
    return (
      <div className="bg-dhs min-h-screen">
        <Header />
        <main className="py-20 text-center">
          <p className="text-[#1F0068] font-bold">Claim not found.</p>
          <Button className="mt-4 rounded-full" onClick={() => nav('/dashboard')}>Back to dashboard</Button>
        </main>
        <Footer />
      </div>
    );
  }

  const stages = ['Submitted', 'Assigned', 'Engineer on the way', 'In progress', 'Resolved'];
  const currentIdx = stages.indexOf(claim.status);

  const addNote = (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    // simulate note stored as an update via advance in a mock way
    claim.updates.push({ at: new Date().toISOString(), text: `Note from you: ${note}` });
    toast({ title: 'Note added', description: 'Our team will see this on their next update.' });
    setNote('');
    // trigger re-render by advancing nothing (small hack)
    nav(`/dashboard/claim/${claim.id}`, { replace: true });
  };

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main className="py-10 md:py-14">
        <div className="max-w-5xl mx-auto px-5">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F0068]/70 hover:text-[#1F0068] mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-3xl p-6 md:p-10 border border-[#1F0068]/5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#FF5C39]">{claim.type} · {claim.id}</div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#1F0068] tracking-tight mt-1">{claim.appliance}</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill status={claim.status} />
                  {(claim.status === 'Engineer on the way' || claim.status === 'Assigned' || claim.status === 'In progress') && (
                    <Link to={`/dashboard/track/${claim.id}`}>
                      <Button className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5 h-9 px-4 text-xs font-bold">
                        Track engineer live →
                      </Button>
                    </Link>
                  )}
                </div>
              </div>

              <p className="mt-5 text-[#1F0068]/80 leading-relaxed">{claim.description}</p>

              {/* Timeline */}
              <div className="mt-8">
                <h3 className="font-bold text-[#1F0068] mb-4">Progress</h3>
                <div className="space-y-3">
                  {stages.map((s, i) => {
                    const done = i < currentIdx;
                    const cur = i === currentIdx;
                    return (
                      <div key={s} className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${done ? 'bg-[#00b67a] text-white' : cur ? 'bg-[#FF5C39] text-white' : 'bg-[#FFF6EB] text-[#1F0068]/40'}`}>
                          {done ? <CheckCircle2 className="w-5 h-5" /> : i + 1}
                        </div>
                        <div className={`flex-1 font-semibold ${done ? 'text-[#1F0068]/70' : cur ? 'text-[#1F0068]' : 'text-[#1F0068]/40'}`}>{s}</div>
                        {cur && <span className="text-xs font-bold text-[#FF5C39]">Current</span>}
                      </div>
                    );
                  })}
                </div>
                {currentIdx < stages.length - 1 && claim.status === 'Engineer on the way' && (
                  <div className="mt-4">
                    <Link to={`/dashboard/track/${claim.id}`}>
                      <Button variant="outline" className="rounded-full border-[#1F0068] text-[#1F0068] hover:bg-[#1F0068] hover:text-white text-xs">
                        Track engineer live →
                      </Button>
                    </Link>
                  </div>
                )}
              </div>

              {/* Updates */}
              <div className="mt-10">
                <h3 className="font-bold text-[#1F0068] mb-4">Activity</h3>
                <div className="space-y-3">
                  {[...claim.updates].reverse().map((u, i) => (
                    <div key={i} className="flex items-start gap-3 bg-[#FFF6EB] rounded-2xl p-4">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#1F0068]">
                        <Clock className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-[#1F0068]">{u.text}</div>
                        <div className="text-xs text-[#1F0068]/60">{new Date(u.at).toLocaleString('en-GB')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add note */}
              <form onSubmit={addNote} className="mt-8">
                <label className="text-[#1F0068] font-semibold flex items-center gap-1.5 mb-2"><MessageSquare className="w-4 h-4" /> Add a note</label>
                <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything else you’d like our team to know?" className="rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 text-[#1F0068]" />
                <div className="mt-3 text-right">
                  <Button type="submit" className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white gap-1.5">
                    Send note <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </div>

            {/* Sidebar */}
            <aside className="space-y-4">
              <div className="bg-white rounded-3xl p-6 border border-[#1F0068]/5">
                <h3 className="font-bold text-[#1F0068] mb-3">Details</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-[#1F0068]/60">Reference</dt>
                    <dd className="font-bold text-[#1F0068]">{claim.id}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-[#1F0068]/60">Type</dt>
                    <dd className="font-semibold text-[#1F0068]">{claim.type}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-[#1F0068]/60">Opened</dt>
                    <dd className="font-semibold text-[#1F0068]">{new Date(claim.createdAt).toLocaleDateString('en-GB')}</dd>
                  </div>
                  {claim.engineer && (
                    <div className="flex items-center justify-between">
                      <dt className="text-[#1F0068]/60">Handler</dt>
                      <dd className="font-semibold text-[#1F0068]">{claim.engineer}</dd>
                    </div>
                  )}
                  {claim.scheduled && (
                    <div className="flex items-center justify-between">
                      <dt className="text-[#1F0068]/60">Scheduled</dt>
                      <dd className="font-semibold text-[#1F0068]">{new Date(claim.scheduled).toLocaleDateString('en-GB')}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="bg-[#1F0068] text-white rounded-3xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><PhoneCall className="w-5 h-5" /></div>
                  <div>
                    <div className="text-xs text-white/70">Need help?</div>
                    <div className="font-bold">0113 548 7557</div>
                  </div>
                </div>
                <p className="mt-3 text-xs text-white/70">Our customer-service team is available Monday to Friday, 9am to 5pm.</p>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ClaimDetail;
